/**
 * Fetches YouTube video metadata and populates episode information
 */

(function() {
  'use strict';

  // Extract YouTube video ID from URL
  function extractYouTubeId(url) {
    if (!url) return null;
    
    let videoId = null;
    
    // Handle different YouTube URL formats
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('watch?v=')[1].split('&')[0].split('?')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split('?')[0].split('&')[0];
    } else if (url.includes('youtube.com/embed/')) {
      videoId = url.split('embed/')[1].split('?')[0].split('&')[0];
    }
    
    return videoId;
  }

  // Fetch YouTube video metadata using oEmbed API
  async function fetchYouTubeMetadata(videoId) {
    if (!videoId) return null;
    
    try {
      const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch YouTube metadata');
      }
      
      const data = await response.json();
      return {
        title: data.title || '',
        description: data.description || '',
        thumbnail: data.thumbnail_url || ''
      };
    } catch (error) {
      console.error('Error fetching YouTube metadata:', error);
      return null;
    }
  }

  // Fetch upload date from YouTube Data API v3 (requires API key)
  async function fetchYouTubeUploadDate(videoId, apiKey) {
    if (!videoId || !apiKey) return null;
    
    try {
      const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${apiKey}&part=snippet`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch YouTube upload date');
      }
      
      const data = await response.json();
      if (data.items && data.items.length > 0) {
        const snippet = data.items[0].snippet;
        return snippet.publishedAt || null; // Returns ISO 8601 date string
      }
      return null;
    } catch (error) {
      console.error('Error fetching YouTube upload date:', error);
      return null;
    }
  }

  // Fetch full description using YouTube Data API (requires API key)
  // This is a fallback if oEmbed doesn't provide full description
  async function fetchYouTubeFullMetadata(videoId, apiKey) {
    if (!videoId || !apiKey) return null;
    
    try {
      const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${apiKey}&part=snippet`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch YouTube full metadata');
      }
      
      const data = await response.json();
      if (data.items && data.items.length > 0) {
        const snippet = data.items[0].snippet;
        return {
          title: snippet.title || '',
          description: snippet.description || '',
          thumbnail: snippet.thumbnails?.maxres?.url || snippet.thumbnails?.high?.url || '',
          publishedAt: snippet.publishedAt || null
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching YouTube full metadata:', error);
      return null;
    }
  }

  // Format date for display
  function formatUploadDate(dateString) {
    if (!dateString) return 'Date not available';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      return dateString;
    }
  }

  // Auto-populate episode data from YouTube
  async function autoPopulateEpisode(episodeElement) {
    // Get YouTube ID from data attribute or extract from URL
    const youtubeId = episodeElement.dataset.youtubeId;
    const youtubeUrl = episodeElement.dataset.youtubeUrl;
    
    if (!youtubeId && !youtubeUrl) return;
    
    const videoId = youtubeId || extractYouTubeId(youtubeUrl);
    if (!videoId) return;

    // Check if title should be auto-filled
    const titleElement = episodeElement.querySelector('.podcast-episode__title[data-auto-fill="true"]');
    const descriptionElement = episodeElement.querySelector('.podcast-episode__description[data-auto-fill="true"]');
    
    // Also check for placeholder text
    const titleElementAny = episodeElement.querySelector('.podcast-episode__title');
    const descriptionElementAny = episodeElement.querySelector('.podcast-episode__description');
    
    const needsTitle = titleElement || 
                      (titleElementAny && (
                        !titleElementAny.textContent.trim() || 
                        titleElementAny.textContent.toLowerCase().includes('loading') ||
                        titleElementAny.textContent.toLowerCase().includes('episode title')
                      ));
    
    const needsDescription = descriptionElement || 
                            (descriptionElementAny && (
                              !descriptionElementAny.textContent.trim() || 
                              descriptionElementAny.textContent.toLowerCase().includes('loading') ||
                              descriptionElementAny.textContent.toLowerCase().includes('episode description')
                            ));

    if (!needsTitle && !needsDescription) {
      return; // Already populated
    }

    // Show loading state
    if (needsTitle && titleElementAny) {
      titleElementAny.textContent = 'Loading...';
    }
    if (needsDescription && descriptionElementAny) {
      descriptionElementAny.textContent = 'Loading description...';
    }

    // Fetch metadata
    const metadata = await fetchYouTubeMetadata(videoId);
    
    if (!metadata) {
      // Restore original if fetch failed
      if (needsTitle && titleElementAny) {
        titleElementAny.textContent = titleElementAny.dataset.originalTitle || '';
      }
      if (needsDescription && descriptionElementAny) {
        descriptionElementAny.textContent = descriptionElementAny.dataset.originalDescription || '';
      }
      return;
    }

    // Update title if needed
    if (needsTitle && titleElementAny && metadata.title) {
      // Store original if not already stored
      if (!titleElementAny.dataset.originalTitle) {
        titleElementAny.dataset.originalTitle = titleElementAny.textContent;
      }
      titleElementAny.textContent = metadata.title.toLowerCase();
    }

    // Update description if needed
    if (needsDescription && descriptionElementAny && metadata.description) {
      // Store original if not already stored
      if (!descriptionElementAny.dataset.originalDescription) {
        descriptionElementAny.dataset.originalDescription = descriptionElementAny.textContent;
      }
      
      // Clean up description (remove URLs, timestamps, etc.)
      let cleanDescription = metadata.description
        .replace(/https?:\/\/[^\s]+/g, '') // Remove URLs
        .replace(/\d+:\d+/g, '') // Remove timestamps like "0:00"
        .replace(/\n{3,}/g, '\n\n') // Remove excessive line breaks
        .replace(/^\s+|\s+$/g, '') // Trim whitespace
        .trim();
      
      // Limit length and get first paragraph
      const firstParagraph = cleanDescription.split('\n\n')[0];
      if (firstParagraph.length > 400) {
        cleanDescription = firstParagraph.substring(0, 400) + '...';
      } else {
        cleanDescription = firstParagraph;
      }
      
      descriptionElementAny.textContent = cleanDescription.toLowerCase();
    }
  }

  // Auto-populate latest episode cards
  async function autoPopulateEpisodeCard(cardElement) {
    const youtubeId = cardElement.dataset.youtubeId;
    const youtubeUrl = cardElement.dataset.youtubeUrl;
    
    if (!youtubeId && !youtubeUrl) return;
    
    const videoId = youtubeId || extractYouTubeId(youtubeUrl);
    if (!videoId) return;

    const titleElement = cardElement.querySelector('.podcast-episode-card__title[data-auto-fill="true"]');
    if (!titleElement) return;

    // Show loading state
    if (titleElement.textContent.toLowerCase().includes('loading')) {
      // Fetch metadata
      const metadata = await fetchYouTubeMetadata(videoId);
      if (metadata && metadata.title) {
        titleElement.textContent = metadata.title;
      }
    }
  }

  // Parse date string to Date object for sorting (DD/MM/YYYY format)
  function parseDateString(dateString) {
    if (!dateString) return null;
    
    try {
      // Parse DD/MM/YYYY format
      const ddmmyyyyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
      const match = dateString.trim().match(ddmmyyyyRegex);
      
      if (match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1; // JavaScript months are 0-indexed
        const year = parseInt(match[3], 10);
        
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
      
      // Fallback: try standard Date parsing
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date;
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  // Sort episodes by upload date
  function sortEpisodesByDate(containerSelector) {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const episodes = Array.from(container.querySelectorAll('.podcast-episode'));
    if (episodes.length === 0) return;

    // Get episodes with their dates
    const episodesWithDates = episodes.map((episode) => {
      const dateString = episode.dataset.uploadDate;
      const date = dateString ? parseDateString(dateString) : null;
      
      return { episode, date };
    });

    // Sort by date (newest first)
    episodesWithDates.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1; // Episodes without dates go to the end
      if (!b.date) return -1;
      return b.date - a.date; // Newest first
    });

    // Reorder episodes in DOM
    episodesWithDates.forEach(({ episode }) => {
      container.appendChild(episode);
    });
  }

  // Initialize episodes visibility and load more functionality
  function initEpisodesVisibility(observeNewEpisodesCallback) {
    const episodesContainer = document.querySelector('.podcast-episodes');
    if (!episodesContainer) return;

    const allEpisodes = Array.from(episodesContainer.querySelectorAll('.podcast-episode'));
    const visibleCount = 5;
    
    // Show first 5 episodes, hide the rest
    allEpisodes.forEach((episode, index) => {
      if (index < visibleCount) {
        episode.classList.remove('podcast-episode--hidden');
      } else {
        episode.classList.add('podcast-episode--hidden');
      }
    });

    // Set up Load More button
    const loadMoreButton = document.getElementById('load-more-episodes');
    if (!loadMoreButton) return;

    // Hide button if there are 5 or fewer episodes
    if (allEpisodes.length <= visibleCount) {
      loadMoreButton.style.display = 'none';
      return;
    }

    // Show button if there are more episodes
    loadMoreButton.style.display = 'block';

    // Remove existing event listener if any (to prevent duplicates)
    const newLoadMoreButton = loadMoreButton.cloneNode(true);
    loadMoreButton.parentNode.replaceChild(newLoadMoreButton, loadMoreButton);

    // Add event listener to the new button
    newLoadMoreButton.addEventListener('click', function() {
      const hiddenEpisodes = Array.from(episodesContainer.querySelectorAll('.podcast-episode--hidden'));
      
      if (hiddenEpisodes.length === 0) {
        // No more episodes to show, hide button
        this.style.display = 'none';
        return;
      }

      // Show next 5 episodes
      hiddenEpisodes.slice(0, visibleCount).forEach(episode => {
        episode.classList.remove('podcast-episode--hidden');
      });

      // Hide button if no more episodes to show
      const remainingHidden = Array.from(episodesContainer.querySelectorAll('.podcast-episode--hidden'));
      if (remainingHidden.length === 0) {
        this.style.display = 'none';
      }

      // Re-observe newly visible episodes for scroll animation
      if (observeNewEpisodesCallback) {
        setTimeout(() => {
          observeNewEpisodesCallback();
        }, 100);
      }
    });
  }

  // Initialize scroll animations for episodes
  function initScrollAnimations() {
    // Check if Intersection Observer is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: show all episodes immediately
      const episodes = document.querySelectorAll('.podcast-episode:not(.podcast-episode--hidden)');
      episodes.forEach(episode => {
        episode.classList.add('podcast-episode--animate');
      });
      return;
    }

    // Create Intersection Observer
    const observerOptions = {
      root: null, // Use viewport as root
      rootMargin: '0px 0px -50px 0px', // Start animation when element is 50px from bottom of viewport
      threshold: 0.15 // Trigger when 15% of element is visible
    };

    let observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('podcast-episode--animate');
          // Stop observing once animation is triggered
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Function to observe visible episodes
    function observeEpisodes() {
      const episodes = document.querySelectorAll('.podcast-episode:not(.podcast-episode--hidden):not(.podcast-episode--animate)');
      episodes.forEach(episode => {
        observer.observe(episode);
      });
    }

    // Initial observation
    observeEpisodes();

    // Return function to re-observe new episodes (for Load More functionality)
    return observeEpisodes;
  }

  // Initialize on page load
  function init() {
    // Find all episode elements with YouTube data
    const episodes = document.querySelectorAll('.podcast-episode[data-youtube-id], .podcast-episode[data-youtube-url]');
    
    episodes.forEach(episode => {
      autoPopulateEpisode(episode);
    });

    // Find all episode cards with YouTube data
    const episodeCards = document.querySelectorAll('.podcast-episode-card[data-youtube-id], .podcast-episode-card[data-youtube-url]');
    
    episodeCards.forEach(card => {
      autoPopulateEpisodeCard(card);
    });

    // Sort episodes by upload date
    sortEpisodesByDate('.podcast-episodes');
    
    // Initialize scroll animations first (returns callback function)
    let observeNewEpisodes = null;
    setTimeout(() => {
      observeNewEpisodes = initScrollAnimations();
    }, 200);
    
    // Initialize episodes visibility after sorting (pass callback for Load More)
    setTimeout(() => {
      initEpisodesVisibility(observeNewEpisodes);
    }, 300);
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run when Shopify theme editor updates
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:section:reorder', init);
  }
})();

